
import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../base/Logo';
import PriceMonitorSidebar from './PriceMonitorSidebar';
import FavoritesSidebar from './FavoritesSidebar';
import ProfileSidebar from './ProfileSidebar';
import AuthModal from './AuthModal';
import { usePriceMonitor } from '../../hooks/usePriceMonitor';
import { useFavorites } from '../../hooks/useFavorites';
import { productsData } from '../../mocks/products';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPriceMonitorOpen, setIsPriceMonitorOpen] = useState(false);
  const [isFavoritesOpen, setIsFavoritesOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [authModal, setAuthModal] = useState<{ isOpen: boolean; view: 'login' | 'register' | 'reset' }>({
    isOpen: false,
    view: 'login',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { getTotalTracked } = usePriceMonitor();
  const { getTotalFavorites } = useFavorites();

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
        setIsSearchExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter products based on search query
  const searchResults =
    searchQuery.length >= 2
      ? productsData
          .filter(
            (p) =>
              p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              p.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
              p.category.toLowerCase().includes(searchQuery.toLowerCase())
          )
          .slice(0, 6)
      : [];

  const handleProductClick = (productId: number) => {
    navigate(`/product/${productId}`);
    setSearchQuery('');
    setShowSearchResults(false);
    setIsSearchExpanded(false);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setShowSearchResults(false);
      setIsSearchExpanded(false);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const handleWishlistClick = () => setIsFavoritesOpen(true);
  const handleUserClick = () => setIsProfileOpen(true);
  const handlePriceMonitorClick = () => setIsPriceMonitorOpen(true);

  return (
    <>
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Logo size="md" />

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              <Link
                to="/"
                className={`font-medium cursor-pointer whitespace-nowrap text-sm ${
                  isActive('/') ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                Home
              </Link>
              <Link
                to="/categories"
                className={`font-medium cursor-pointer whitespace-nowrap text-sm ${
                  isActive('/categories') ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                Categories
              </Link>
              <Link
                to="/shop"
                className={`font-medium cursor-pointer whitespace-nowrap text-sm ${
                  isActive('/shop') ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                Compare Prices
              </Link>
              <Link
                to="/deals"
                className={`font-medium cursor-pointer whitespace-nowrap flex items-center gap-1.5 text-sm ${
                  isActive('/deals') ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                <i className="ri-fire-fill text-orange-500"></i>
                Deals
              </Link>
              <Link
                to="/compare"
                className={`font-medium cursor-pointer whitespace-nowrap flex items-center gap-1.5 text-sm ${
                  isActive('/compare') ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                <i className="ri-scales-3-line text-teal-500"></i>
                Compare
              </Link>
            </nav>

            {/* Search Bar */}
            <div ref={searchRef} className="hidden md:block relative">
              <form onSubmit={handleSearchSubmit}>
                <div className={`relative transition-all duration-300 ${isSearchExpanded ? 'w-80' : 'w-64'}`}>
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowSearchResults(true);
                    }}
                    onFocus={() => {
                      setIsSearchExpanded(true);
                      if (searchQuery.length >= 2) setShowSearchResults(true);
                    }}
                    placeholder="Search products..."
                    className="w-full pl-10 pr-4 py-2 bg-gray-100 border border-transparent rounded-full text-sm focus:outline-none focus:bg-white focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-all"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery('');
                        setShowSearchResults(false);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              </form>

              {/* Search Results Dropdown */}
              {showSearchResults && searchQuery.length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
                  {searchResults.length > 0 ? (
                    <>
                      <div className="p-2">
                        {searchResults.map((product) => (
                          <button
                            key={product.id}
                            onClick={() => handleProductClick(product.id)}
                            className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors text-left"
                          >
                            <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                              <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                              <p className="text-xs text-gray-500">
                                {product.brand} • {product.category}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-teal-600">${product.currentPrice}</p>
                              {product.priceChange && product.priceChange < 0 && (
                                <p className="text-xs text-green-600">{product.priceChange}%</p>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                      <div className="border-t border-gray-100 p-2">
                        <button
                          onClick={handleSearchSubmit}
                          className="w-full py-2 text-sm text-teal-600 hover:bg-teal-50 rounded-lg cursor-pointer transition-colors font-medium"
                        >
                          View all results for &quot;{searchQuery}&quot;
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="p-6 text-center">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-search-line text-xl text-gray-400"></i>
                      </div>
                      <p className="text-sm text-gray-500">No products found for &quot;{searchQuery}&quot;</p>
                      <p className="text-xs text-gray-400 mt-1">Try a different search term</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right Icons */}
            <div className="flex items-center space-x-2">
              <div className="hidden md:flex items-center space-x-1">
                <button
                  onClick={handleWishlistClick}
                  className="p-2 text-gray-600 hover:text-gray-900 cursor-pointer relative"
                >
                  <i className="ri-heart-line text-xl"></i>
                  {getTotalFavorites() > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {getTotalFavorites()}
                    </span>
                  )}
                </button>
                <button
                  onClick={handlePriceMonitorClick}
                  className="p-2 text-gray-600 hover:text-gray-900 cursor-pointer relative"
                >
                  <i className="ri-line-chart-line text-xl"></i>
                  {getTotalTracked() > 0 && (
                    <span className="absolute -top-1 -right-1 bg-teal-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {getTotalTracked()}
                    </span>
                  )}
                </button>
                <button
                  onClick={handleUserClick}
                  className="p-2 text-gray-600 hover:text-gray-900 cursor-pointer"
                >
                  <i className="ri-user-line text-xl"></i>
                </button>
                <div className="w-px h-6 bg-gray-200 mx-1"></div>
                <button
                  onClick={() => setAuthModal({ isOpen: true, view: 'login' })}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer whitespace-nowrap"
                >
                  Sign In
                </button>
                <button
                  onClick={() => setAuthModal({ isOpen: true, view: 'register' })}
                  className="px-3 py-1.5 text-sm font-medium bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg hover:from-teal-600 hover:to-emerald-600 cursor-pointer whitespace-nowrap"
                >
                  Sign Up
                </button>
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2 text-gray-600 hover:text-gray-900 cursor-pointer"
              >
                <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t">
              {/* Mobile Search */}
              <form onSubmit={handleSearchSubmit} className="mb-4">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search products..."
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-100 border border-transparent rounded-lg text-sm focus:outline-none focus:bg-white focus:border-teal-500"
                  />
                </div>
              </form>

              <nav className="flex flex-col space-y-4">
                <Link
                  to="/"
                  className={`font-medium cursor-pointer ${isActive('/') ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </Link>
                <Link
                  to="/shop"
                  className={`font-medium cursor-pointer ${isActive('/shop') ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Compare Prices
                </Link>
                <Link
                  to="/compare"
                  className={`font-medium cursor-pointer flex items-center gap-1.5 ${
                    isActive('/compare') ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="ri-scales-3-line text-teal-500"></i>
                  Compare Products
                </Link>
                <Link
                  to="/deals"
                  className={`font-medium cursor-pointer flex items-center gap-1.5 ${
                    isActive('/deals') ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="ri-fire-fill text-orange-500"></i>
                  Deals
                </Link>
                <Link
                  to="/notifications"
                  className={`font-medium cursor-pointer flex items-center gap-1.5 ${
                    isActive('/notifications') ? 'text-gray-900' : 'text-gray-700 hover:text-gray-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="ri-notification-3-line text-teal-500"></i>
                  Alerts
                </Link>
              </nav>
              <div className="flex items-center space-x-4 mt-4 pt-4 border-t">
                <button
                  onClick={() => {
                    handleWishlistClick();
                    setIsMenuOpen(false);
                  }}
                  className="p-2 text-gray-600 hover:text-gray-900 cursor-pointer relative"
                >
                  <i className="ri-heart-line text-xl"></i>
                  {getTotalFavorites() > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {getTotalFavorites()}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => {
                    handlePriceMonitorClick();
                    setIsMenuOpen(false);
                  }}
                  className="p-2 text-gray-600 hover:text-gray-900 cursor-pointer relative"
                >
                  <i className="ri-line-chart-line text-xl"></i>
                  {getTotalTracked() > 0 && (
                    <span className="absolute -top-1 -right-1 bg-teal-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {getTotalTracked()}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => {
                    handleUserClick();
                    setIsMenuOpen(false);
                  }}
                  className="p-2 text-gray-600 hover:text-gray-900 cursor-pointer"
                >
                  <i className="ri-user-line text-xl"></i>
                </button>
              </div>
              <div className="flex gap-3 mt-4 pt-4 border-t">
                <button
                  onClick={() => {
                    setAuthModal({ isOpen: true, view: 'login' });
                    setIsMenuOpen(false);
                  }}
                  className="flex-1 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg cursor-pointer whitespace-nowrap"
                >
                  Sign In
                </button>
                <button
                  onClick={() => {
                    setAuthModal({ isOpen: true, view: 'register' });
                    setIsMenuOpen(false);
                  }}
                  className="flex-1 py-2 text-sm font-medium bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg cursor-pointer whitespace-nowrap"
                >
                  Sign Up
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Price Monitor Sidebar */}
      <PriceMonitorSidebar isOpen={isPriceMonitorOpen} onClose={() => setIsPriceMonitorOpen(false)} />

      {/* Favorites Sidebar */}
      <FavoritesSidebar isOpen={isFavoritesOpen} onClose={() => setIsFavoritesOpen(false)} />

      {/* Profile Sidebar */}
      <ProfileSidebar isOpen={isProfileOpen} onClose={() => setIsProfileOpen(false)} />

      {/* Auth Modal */}
      <AuthModal
        isOpen={authModal.isOpen}
        onClose={() => setAuthModal({ ...authModal, isOpen: false })}
        initialView={authModal.view}
      />
    </>
  );
};

export default Header;
